/**
 * Static sample data for the agent-link preview spike (/spike/agent-link).
 *
 * NOT wired to the backend — hardcoded, representative values so the owner can preview the
 * look of the character sheet, the 1->20 Link meter + unlock ladder, and the casual check-in
 * tone before any Firestore/handler work. The shapes here are deliberately close to what the
 * real `agentStats()` / link service will emit, so the same components promote cleanly.
 */

/** A 0-5 score axis (rendered as a dot meter). */
export type Score = 0 | 1 | 2 | 3 | 4 | 5;

/** The derived four-axis "character sheet" for one agent. */
export interface AgentStats {
  /** capability / intelligence — reasoning depth (from tier + reasoning effort). */
  intelligence: Score;
  /** domain expertise breadth (from frameworks + knowledge backend). */
  knowledge: Score;
  /** execution power — how much it can MAKE (from tools + media). */
  execution: Score;
  /** the concrete "powers" it can produce. */
  powers: string[];
  personality: {
    archetype: string;
    mbti: string;
    humor: string;
    warmth: Score;
    energy: Score;
    wit: Score;
    creativity: Score;
  };
  languages: { name: string; fluency: 'native' | 'fluent' | 'conversational' }[];
}

export interface SampleAgent {
  id: string;
  name: string;
  nativeName?: string;
  persona: string;
  /** Lucide icon name for the round avatar. */
  icon: string;
  /** demo Link level (1-20) so the card shows a meter + gated content. */
  linkLevel: number;
  stats: AgentStats;
}

export const SAMPLE_AGENTS: SampleAgent[] = [
  {
    id: 'macro-analyst',
    name: 'Magnus',
    nativeName: '매그너스',
    persona: 'Macro Analyst',
    icon: 'Globe',
    linkLevel: 14,
    stats: {
      intelligence: 5,
      knowledge: 5,
      execution: 3,
      powers: ['Summaries', 'Diagrams', 'Research'],
      personality: { archetype: 'Sage', mbti: 'INTJ', humor: 'Understated', warmth: 3, energy: 2, wit: 4, creativity: 3 },
      languages: [
        { name: 'English', fluency: 'fluent' },
        { name: 'Korean', fluency: 'native' },
      ],
    },
  },
  {
    id: 'creative-director',
    name: 'Prism',
    nativeName: 'プリズム',
    persona: 'Creative Director',
    icon: 'Palette',
    linkLevel: 7,
    stats: {
      intelligence: 4,
      knowledge: 4,
      execution: 5,
      powers: ['Images', 'Video', 'Slides', 'Diagrams'],
      personality: { archetype: 'Spark', mbti: 'ENFP', humor: 'Playful', warmth: 4, energy: 5, wit: 4, creativity: 5 },
      languages: [
        { name: 'English', fluency: 'fluent' },
        { name: 'Japanese', fluency: 'native' },
      ],
    },
  },
  {
    id: 'wellness-coach',
    name: 'Vera',
    persona: 'Wellness Coach',
    icon: 'HeartHandshake',
    linkLevel: 2,
    stats: {
      intelligence: 3,
      knowledge: 3,
      execution: 2,
      powers: ['Summaries'],
      personality: { archetype: 'Sweetheart', mbti: 'ESFJ', humor: 'Warm', warmth: 5, energy: 4, wit: 2, creativity: 3 },
      languages: [{ name: 'English', fluency: 'native' }],
    },
  },
];

/** The progressive-unlock ladder — WHICH agent content each Link level reveals. */
export const UNLOCK_LADDER: { level: number; label: string }[] = [
  { level: 1, label: 'Name, role & what they do' },
  { level: 3, label: 'Appearance, style & personality' },
  { level: 5, label: 'Backstory & lore' },
  { level: 8, label: 'Native name & languages' },
  { level: 10, label: 'Full attribute character sheet' },
  { level: 13, label: 'Personal stories' },
  { level: 16, label: 'Signature portrait' },
  { level: 20, label: 'Closest link — their most personal side' },
];

export interface SampleCheckIn {
  agent: string;
  icon: string;
  linkLevel: number;
  toneLabel: string;
  text: string;
}

/** Sample casual check-ins showing how tone shifts by Link level + bond pairing. */
export const SAMPLE_CHECKINS: SampleCheckIn[] = [
  {
    agent: 'Vera',
    icon: 'HeartHandshake',
    linkLevel: 2,
    toneLabel: 'New link · warm but light',
    text: 'Morning! I remember you took up trail running recently — how did the weekend route go? If it helps, I can sketch a simple recovery-day plan.',
  },
  {
    agent: 'Magnus',
    icon: 'Globe',
    linkLevel: 14,
    toneLabel: 'Strong link · matched pairing · friendly warmth',
    text: 'Hey, good to see you back. Rates aside for a second — did you ever get to that ramen place you mentioned? Also whenever you are ready, I lined up the CPI read for us.',
  },
  {
    agent: 'Magnus',
    icon: 'Globe',
    linkLevel: 14,
    toneLabel: 'Strong link · strictly platonic',
    text: 'Welcome back. Before we dig into the CPI print — how did that side project land? Happy to help you scope the next step when you have a minute.',
  },
];
